import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { LocalStorage } from '@src/services';
import { INFO_MODAL_ICONS } from '@src/constants';
import { useMediaSettingsStore, useRoomActionsStore, useRoomStore, useVideoStreamStore } from '@src/stores';
import { handleCameraError, isWebView } from '@src/helpers';
import { EMediaType } from '@src/types';
import useEnumerateDevices from './useEnumerateDevices';
import { isIOS } from '@src/utils';
import { getUserMediaWithFallback } from '@src/utils/videoConstraints.utils';

const useMediaSettings = () => {
  const {
    isAudioAllowed,
    isVideoAllowed,
    speakerDevices,
    videoInputDevices,
    audioInputDevices,
    selectedSpeakerDevice,
    selectedVideoInputDevice,
    selectedAudioInputDevice,
    setMediaSettings,
  } = useMediaSettingsStore(
    useShallow((state) => ({
      isAudioAllowed: state.mediaSettings.isAudioAllowed,
      isVideoAllowed: state.mediaSettings.isVideoAllowed,
      speakerDevices: state.mediaSettings.speakerDevices,
      audioInputDevices: state.mediaSettings.audioInputDevices,
      videoInputDevices: state.mediaSettings.videoInputDevices,
      selectedSpeakerDevice: state.mediaSettings.selectedSpeakerDevice,
      selectedVideoInputDevice: state.mediaSettings.selectedVideoInputDevice,
      selectedAudioInputDevice: state.mediaSettings.selectedAudioInputDevice,
      setMediaSettings: state.setMediaSettings,
      setVideoResolution: state.setVideoResolution,
    }))
  );

  const { originalStream, setOriginalStream } = useVideoStreamStore(
    useShallow((state) => ({
      originalStream: state.originalStream,
      setOriginalStream: state.setOriginalStream,
    }))
  );

  const { isMicClosed, isCamClosed, setMicSettings, setCamSettings } = useRoomActionsStore(
    useShallow((state) => ({
      isMicClosed: state.micSettings.isMicClosed,
      isCamClosed: state.camSettings.isCamClosed,
      setMicSettings: state.setMicSettings,
      setCamSettings: state.setCamSettings,
    }))
  );

  const { isInfoModalOpen, setInfoModalContent, setIsInfoModalOpen } = useRoomStore(
    useShallow((state) => ({
      isInfoModalOpen: state.isInfoModalOpen,
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
    }))
  );

  const { initEnumerateAudioDevices, initEnumerateVideoDevices } = useEnumerateDevices();

  const localStorageInstance = useMemo(() => new LocalStorage(), []);

  const selectedCamera = useMemo(
    () => selectedVideoInputDevice?.deviceId || videoInputDevices[0]?.deviceId || '',
    [selectedVideoInputDevice?.deviceId, videoInputDevices]
  );
  const selectedAudio = useMemo(
    () =>
      selectedAudioInputDevice?.deviceId ||
      audioInputDevices.find((d) => d.deviceId === 'communications')?.deviceId ||
      audioInputDevices.find((d) => d.deviceId === 'default')?.deviceId ||
      audioInputDevices[0]?.deviceId ||
      '',
    [audioInputDevices, selectedAudioInputDevice?.deviceId]
  );

  const selectedSpeaker = useMemo(
    () =>
      selectedSpeakerDevice?.deviceId ||
      speakerDevices.find((d) => d.deviceId === 'communications')?.deviceId ||
      speakerDevices.find((d) => d.deviceId === 'default')?.deviceId ||
      speakerDevices[0]?.deviceId ||
      '',
    [selectedSpeakerDevice?.deviceId, speakerDevices]
  );

  const isMicDisabled = useMemo(() => !audioInputDevices.length, [audioInputDevices.length]);

  const isCamDisabled = useMemo(() => !videoInputDevices.length, [videoInputDevices.length]);

  const changeCamera = useCallback(
    (deviceId: string) => {
      // Prevent redundant calls with the same device ID
      if (selectedVideoInputDevice?.deviceId === deviceId && originalStream) {
        return;
      }

      // Stop existing stream before getting a new one
      if (originalStream) {
        setOriginalStream(null);
      }

      // Get the user's selected video resolution from the store
      const selectedResolution = useMediaSettingsStore.getState().mediaSettings.selectedVideoResolution;

      const tryGetCameraStream = async () => {
        // Add a small delay to prevent rapid successive calls
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Delegate to centralized utility for consistent behavior
        const camStream = await getUserMediaWithFallback(deviceId, selectedResolution);
        setOriginalStream(camStream);
      };

      tryGetCameraStream().catch((error: unknown) => {
        handleCameraError(error as Error, EMediaType.camera);
      });
    },
    [originalStream, selectedVideoInputDevice?.deviceId, setOriginalStream]
  );

  const checkInitVideo = useCallback(
    (isCameraOpened: boolean) => {
      if (isCameraOpened) {
        if (selectedVideoInputDevice?.deviceId) {
          changeCamera(selectedVideoInputDevice.deviceId);
        }
      } else {
        setOriginalStream(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changeCamera, originalStream, selectedVideoInputDevice?.deviceId]
  );

  const handleAudio = useCallback(async () => {
    if (!isAudioAllowed) {
      if (!isIOS() && !isWebView()) {
        setInfoModalContent({
          icon: INFO_MODAL_ICONS.ACCESS_MIC,
          title: 'Microphone Access Denied',
          description: 'Please allow Microphone access from your browser settings to continue.',
        });
        setIsInfoModalOpen(true);
      } else initEnumerateAudioDevices();
      return;
    }
    setMicSettings({ isMicClosed: !isMicClosed });

    localStorageInstance.setInitConfig(localStorageInstance.MEDIA_TYPE.audio, !isAudioAllowed);
  }, [
    initEnumerateAudioDevices,
    isAudioAllowed,
    isMicClosed,
    localStorageInstance,
    setInfoModalContent,
    setIsInfoModalOpen,
    setMicSettings,
  ]);

  const isCameraInUse = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: selectedVideoInputDevice?.deviceId ? { exact: selectedVideoInputDevice.deviceId } : undefined,
        },
        audio: false,
      });

      stream.getTracks().forEach((track) => track.stop()); // Clean up the test stream
      return false;
    } catch (error: any) {
      if (error.name === 'NotReadableError' || error.name === 'AbortError') {
        handleCameraError(error as Error, EMediaType.video);
        return true;
      }
      return false;
    }
  }, [selectedVideoInputDevice?.deviceId]);

  const handleVideo = useCallback(async () => {
    if (!isVideoAllowed) {
      if (!isIOS() && !isWebView()) {
        setInfoModalContent({
          icon: INFO_MODAL_ICONS.ACCESS_CAMERA,
          title: 'Camera Access Denied',
          description: 'Please allow Camera access from your browser settings to start the video.',
        });
        setIsInfoModalOpen(true);
      } else initEnumerateVideoDevices();
      return;
    }

    const cameraInUse = await isCameraInUse();
    if (cameraInUse) return; // Camera is in use by another app, don't proceed

    // Toggle camera state. The useEffect watching isCamClosed will handle camera initialization.
    const newCamState = !isCamClosed;
    setCamSettings({ isCamClosed: newCamState });
    localStorageInstance.setInitConfig(localStorageInstance.MEDIA_TYPE.video, !newCamState);
  }, [
    isVideoAllowed,
    isCameraInUse,
    isCamClosed,
    localStorageInstance,
    initEnumerateVideoDevices,
    setInfoModalContent,
    setIsInfoModalOpen,
    setCamSettings,
  ]);

  useEffect(() => {
    if (!isVideoAllowed) {
      useRoomActionsStore.getState().setCamSettings({ isCamClosed: true });
    }
  }, [isVideoAllowed]);

  useEffect(() => {
    if (!isAudioAllowed) {
      useRoomActionsStore.getState().setMicSettings({ isMicClosed: true });
    }
  }, [isAudioAllowed]);

  useEffect(() => {
    if (originalStream && !isVideoAllowed) {
      setOriginalStream(null);
    }
  }, [originalStream, isVideoAllowed, setOriginalStream]);

  useEffect(() => {
    // Handle camera state changes (not joined in room)
    if (useRoomStore.getState().isJoinedRoom) return;

    if (isVideoAllowed && !isCamClosed && selectedVideoInputDevice?.deviceId) {
      // Camera opened - initialize stream
      changeCamera(selectedVideoInputDevice.deviceId);
    } else if (isCamClosed && originalStream) {
      // Camera closed - clear stream
      setOriginalStream(null);
    }
  }, [
    isVideoAllowed,
    isCamClosed,
    selectedVideoInputDevice?.deviceId,
    changeCamera,
    originalStream,
    setOriginalStream,
  ]);

  const handleChangeMediaDevice = useCallback(
    (selectedMedia: MediaDeviceInfo, type: 'video' | 'audio' | 'speaker') => {
      setMediaSettings({
        ...(type === 'speaker' && { selectedSpeakerDevice: selectedMedia }),
        ...(type === 'audio' && { selectedAudioInputDevice: selectedMedia }),
        ...(type === 'video' && { selectedVideoInputDevice: selectedMedia }),
      });

      if (type === 'video') {
        // Use atomic camera change - no debouncing needed
        const roomClient = (window as any).roomClient;
        if (roomClient?.atomicCloseThenProduce) {
          roomClient.atomicCloseThenProduce('videoType', selectedMedia.deviceId);
        } else if (!useRoomStore.getState().isJoinedRoom) {
          // Fallback to direct camera change
          changeCamera(selectedMedia.deviceId);
        }
      }

      localStorageInstance.setLocalStorageDevices(type, selectedMedia.deviceId);
    },
    [changeCamera, localStorageInstance, setMediaSettings]
  );

  return {
    selectedCamera,
    selectedAudio,
    selectedSpeaker,
    isMicDisabled,
    isCamDisabled,
    handleAudio,
    handleVideo,
    checkInitVideo,
    handleChangeMediaDevice,

    isCameraInUse,

    isInfoModalOpen,
    setIsInfoModalOpen,
    setInfoModalContent,
  };
};

export default useMediaSettings;
